// LEGACY: reproduces the data orchestration the old App.vue ran on mount —
// the get_documents fan-out, ticket loading and user fetch that the legacy
// views (Home, Debt, Summary, …) read from Vuex.
// Carries NO token handling: refresh = useAuth, idle = useIdleTimeout,
// cache lifecycle + logout redirect = useAuthEffects. Its only auth coupling
// is READING the identity (token_id) as a data trigger.
// Shrinks per migrated view; deleted when the last legacy view is migrated.
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { watchDebounced } from '@vueuse/core'
import useAuthStore from '@/stores/authStore'
import { refreshToken } from '@/composables/useAuth'
import { setDatadogUserAndAccount } from '@/plugins/datadog'
import { identifySurvicateUser } from '@/plugins/survicate'
import { $eventBusKey } from '@/utils/eventBus'
import { store as legacyStoreUntyped } from '@old/store'

interface LegacyUser {
  _id?: string
  _created?: string
  email_data?: { email?: string }
  [key: string]: unknown
}

interface LegacyPrintDocument {
  _id?: string
  letter?: string
  ocr_number?: string | null
  due_date?: string
  print?: { id?: string }
  print_data?: Record<string, Record<string, unknown> | undefined>
  [key: string]: unknown
}

interface LegacyItem {
  _id?: string
  reference?: string
  closed?: unknown
  status?: unknown
  amount?: unknown
  case?: string
  print?: Record<string, unknown>
  print_document?: LegacyPrintDocument
  ocr?: LegacyItem
  cases?: LegacyItem[]
  debt_collections?: LegacyItem[]
  enforcements?: LegacyItem[]
  latest_advice?: string
  latest_advice_document?: unknown
  [key: string]: unknown
}

interface EveMeta {
  total?: number
  page?: string
  max_results?: number
}

interface EveList {
  _items: LegacyItem[]
  _meta: EveMeta
}

interface LegacyVuexStore {
  state: {
    user: LegacyUser
    documents: LegacyItem[]
    remove_document_ids: string[]
    [key: string]: unknown
  }
  dispatch(action: 'set_state_property', payload: { property: string; data: unknown }): Promise<unknown>
}

const store = legacyStoreUntyped as LegacyVuexStore

// ---------------------------------------------------------------------------
// Legacy fetch helpers — ported from src_old api_mixin (hand-written fetch,
// NOT the new openapi-fetch layer). Token is read from
// authStore and refreshed via the single refresh implementation.
// ---------------------------------------------------------------------------

async function getValidToken(): Promise<string> {
  const authStore = useAuthStore()
  const exp = authStore.selectedToken?.payload?.exp
  // 30s skew, same as legacy token_helper.token_expired
  if (exp != null && Date.now() >= (exp - 30) * 1000) {
    await refreshToken()
  }
  return authStore.selectedToken?.token ?? ''
}

function buildUrl(url: string, params?: Record<string, unknown>): string {
  const searchParams = new URLSearchParams()
  const path = url.startsWith('/') ? url : `/${url}`
  for (const key in params) {
    const value = params[key]
    if (typeof value === 'object' && value != null) {
      searchParams.append(key, JSON.stringify(value))
    } else {
      searchParams.append(key, String(value))
    }
  }
  const queryString = searchParams.toString()
  return `${import.meta.env.VITE_APP_API_ROOT}${path}${queryString ? `?${queryString}` : ''}`
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- T types the response at each call site
async function apiGet<T>(url: string, params?: Record<string, unknown>): Promise<{ status: number; data: T }> {
  const response = await fetch(buildUrl(url, params), {
    headers: {
      'X-API-Key': await getValidToken(),
      'Cache-Control': 'no-cache',
    },
  })
  if (!response.ok) {
    throw new Error(`Legacy bootstrap GET ${url} failed with ${String(response.status)}`)
  }
  const data = (await response.json()) as T
  return { status: response.status, data }
}

async function apiGetAllItems(url: string, params?: Record<string, unknown>): Promise<LegacyItem[]> {
  const items: LegacyItem[] = []
  let page = 1
  let total: number | undefined = undefined

  for (;;) {
    const request = await apiGet<EveList>(url, { ...params, page })
    total ??= request.data._meta.total ?? 0
    items.push(...request.data._items)
    if (items.length >= total || request.data._items.length === 0) break
    page += 1
  }
  return items
}

// ---------------------------------------------------------------------------
// Document fan-out + helpers (verbatim port of old App.vue methods)
// ---------------------------------------------------------------------------

async function addPrintAndReferenceToItems(items: LegacyItem[] | undefined, reference: string) {
  if (items == null || items.length === 0) return

  const where = {
    $in: items.map((v) => v._id ?? v.reference),
  }
  const prints = await apiGetAllItems('/prints', {
    where: { [reference]: where },
    sort: '-_created',
  })
  for (const item of items) {
    item.reference = reference

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- narrows the index-signature item to the print shape
    const printDocument = prints.find((v) => v[reference] === item._id) as LegacyPrintDocument | undefined
    if (printDocument == null) continue

    const letter = printDocument.letter
    const printData = letter != null ? printDocument.print_data?.[letter] : undefined
    if (printData == null) continue

    if (printDocument.print != null) {
      printData.bucket_id = printDocument.print.id
      printData.print_id = printDocument._id
    }
    item.print = printData
    item.print_document = printDocument
  }
}

async function getOcrNumbersForDocumentsMissingPrint(documents: LegacyItem[]) {
  const documentsMissingPrints = documents.filter((v) => v.print_document?.ocr_number == null)
  for (const document of documentsMissingPrints) {
    if (document.reference == null || !['case', 'dispute'].includes(document.reference)) continue
    const response = await apiGet<EveList>('/ocr-numbers', {
      where: {
        [`${document.reference}.${document.reference}`]: document._id,
      },
    })
    if (response.status === 200) {
      const [item] = response.data._items
      if (item != null) {
        document.ocr = item
      }
    }
  }
}

async function getOcrNumbers(documents: LegacyItem[]) {
  const where = {
    $in: documents.map((v) => v.print_document?.ocr_number).filter((ocrNumber) => ocrNumber != null),
  }
  const items = await apiGetAllItems('/ocr-numbers', {
    where: { _id: where },
  })
  for (const item of items) {
    const document = documents.find((v) => v.print_document?.ocr_number === item._id)
    if (document) {
      document.ocr = item
    }
  }

  await getOcrNumbersForDocumentsMissingPrint(documents)
}

async function getAmortizationAdvice(amortization: LegacyItem) {
  const adviceId = amortization.latest_advice
  if (adviceId == null) return
  const response = await apiGet<LegacyItem>(`/customer--amortization-advices/${adviceId}`)
  if (response.status === 200) {
    amortization.latest_advice_document = response.data
    const status = ['queue', 'awaiting_payment', 'partially_paid'].includes(response.data.status as string)
      ? 'unpaid'
      : 'paid'

    if (amortization.ocr != null) {
      amortization.ocr.status = status
      amortization.ocr.amount = response.data.amount
    }
  }
}

function handleAmortizations(amortizations: LegacyItem[]) {
  for (const amortization of amortizations) {
    if (amortization.latest_advice != null) {
      void getAmortizationAdvice(amortization)
    }
  }
}

function sortDocuments(documents: LegacyItem[]) {
  documents.sort((a, b) => {
    const dates: [Date, Date] = [new Date(), new Date()]

    for (const [idx, doc] of [a, b].entries()) {
      if (doc.reference === 'enforcement') continue
      dates[idx as 0 | 1] = new Date(doc.print_document?.due_date ?? '')
    }

    const [dateA, dateB] = dates
    return dateA.getTime() - dateB.getTime()
  })
}

async function getCases(): Promise<LegacyItem[]> {
  const items = await apiGetAllItems('/cases', {
    where: {
      state: { $ne: 'initializing' },
      debt_collection: null,
      enforcement: null,
      amortization: null,
      debt_surveillance: null,
      debt_restructuring: null,
      estate: null,
      dispute: null,
    },
  })
  await addPrintAndReferenceToItems(items, 'case')
  await getOcrNumbers(items)
  return items
}

async function getDebtCollections(): Promise<LegacyItem[]> {
  const items = await apiGetAllItems('/case--debt-collections', {
    where: {
      enforcement: null,
      amortization: null,
      debt_surveillance: null,
      debt_restructuring: null,
      estate: null,
      dispute: null,
    },
  })
  await addPrintAndReferenceToItems(items, 'debt_collection')
  await getOcrNumbers(items)
  return items
}

async function getEnforcements(): Promise<LegacyItem[]> {
  const items = await apiGetAllItems('/case--enforcements', {
    where: {
      amortization: null,
      debt_restructuring: null,
      debt_surveillance: null,
      estate: null,
    },
  })
  await addPrintAndReferenceToItems(items, 'enforcement')
  await getOcrNumbers(items)
  return items
}

async function getDebtSurveillance(): Promise<LegacyItem[]> {
  const authStore = useAuthStore()
  const [debtorId] = authStore.selectedToken?.payload?.d_id ?? []
  const response = await apiGet<EveList>('/debtor--debt-surveillances', {
    where: {
      debtor: debtorId,
    },
  })
  if (response.status === 200 && (response.data._meta.total ?? 0) > 0) {
    const [debtSurveillance] = response.data._items
    if (debtSurveillance == null) return []

    if (
      !debtSurveillance.cases?.length &&
      !debtSurveillance.debt_collections?.length &&
      !debtSurveillance.enforcements?.length
    ) {
      return [] // We do not show debt surveillance if it is empty
    }

    await addPrintAndReferenceToItems([debtSurveillance], 'debt_surveillance')
    await getOcrNumbers([debtSurveillance])

    await addPrintAndReferenceToItems(debtSurveillance.cases, 'case')
    await addPrintAndReferenceToItems(debtSurveillance.debt_collections, 'debt_collection')
    await addPrintAndReferenceToItems(debtSurveillance.enforcements, 'enforcement')

    return [debtSurveillance]
  }
  return []
}

async function getAmortizations(): Promise<LegacyItem[]> {
  const items = await apiGetAllItems('/customer--amortizations', {
    where: {
      $or: [
        {
          status: { $in: ['amortization', 'closed'] },
        },
        {
          amortization_arrangement: { $exists: true },
        },
      ],
    },
  })
  await addPrintAndReferenceToItems(items, 'amortization')
  await getOcrNumbers(items)
  handleAmortizations(items)
  return items
}

async function getDisputes(): Promise<LegacyItem[]> {
  const items = await apiGetAllItems('/customer--disputes', {
    where: {},
  })
  await addPrintAndReferenceToItems(items, 'dispute')
  await getOcrNumbers(items)
  return items
}

export function useLegacyBootstrap() {
  const authStore = useAuthStore()
  const route = useRoute()
  const router = useRouter()
  const bus = inject($eventBusKey)
  const loading = ref(false)

  function setStateProperty(property: string, data: unknown) {
    return store.dispatch('set_state_property', { property, data })
  }

  async function sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  // Guards resolve route.name asynchronously — wait it out (old App.vue behavior)
  async function routerInitialization() {
    while (route.name === undefined) {
      await sleep(100)
    }
  }

  async function getDirectDebitPayments() {
    const directDebitPayments = await apiGetAllItems('/customer--direct-debit-payments')

    const byCase: Record<string, LegacyItem> = {}
    for (const payment of directDebitPayments) {
      if (payment.case != null) byCase[payment.case] = payment
    }
    await setStateProperty('direct_debit_payments', byCase)
  }

  async function getDocuments() {
    loading.value = true
    try {
      const results = await Promise.all([
        getCases(),
        getDebtCollections(),
        getEnforcements(),
        getDebtSurveillance(),
        getAmortizations(),
        getDisputes(),
      ])

      const documents: LegacyItem[] = []
      const closedDocuments: LegacyItem[] = []

      for (const items of results) {
        for (const item of items) {
          if (item.closed && item.status === 'closed') {
            closedDocuments.push(item)
          } else {
            documents.push(item)
          }
        }
      }

      sortDocuments(documents)
      await getDirectDebitPayments()

      await setStateProperty('documents', documents)
      await setStateProperty('closed_documents', closedDocuments)
    } finally {
      loading.value = false
    }
  }

  async function getTickets() {
    const items = await apiGetAllItems('tickets', { sort: '-_created' })
    await setStateProperty('tickets', items)
  }

  async function getUnreadTickets() {
    const response = await apiGet<EveList>('tickets', {
      where: { read_by_requester: false },
      sort: '-_updated',
      limit: 1,
    })
    await setStateProperty('unread_tickets', response.data._meta.total)
  }

  function loadTicketData() {
    void getTickets()
    void getUnreadTickets()
  }

  async function getUserData() {
    try {
      const response = await apiGet<EveList>('/customer--users')
      if (response.status === 200) {
        const [item] = response.data._items
        await setStateProperty('user', item ?? {})
      }
    } catch (err) {
      console.error(err)
    }
  }

  // From old App.vue created()
  void setStateProperty('dont_show_profile_dialog', sessionStorage.getItem('dont_show_profile_dialog') === 'true')

  // The ONLY auth coupling: read the identity to know when to (re)load legacy
  // data, plus legacy navigation on identity switch (dies with the old views).
  // Watches the payload (not just token_id) so token refreshes also fire —
  // needed for the user-fetch retry below.
  watch(
    () => authStore.selectedToken?.payload ?? null,
    async (newPayload, oldPayload) => {
      await routerInitialization()
      const newId = newPayload?.token_id ?? null
      const oldId = oldPayload?.token_id ?? null
      if (newId == null) return

      if (newId === oldId) {
        // Same identity, token event (e.g. hourly refresh). Legacy retried the
        // user fetch on any token event while user._id was still empty — a
        // failed first fetch must not leave the profile/analytics identity
        // empty for the whole session.
        if (store.state.user._id == null) await getUserData()
        return
      }

      if (route.name === 'Authenticate' && route.query.token) {
        // Avoid fetching documents. Allow authenticate to load new token
        return
      }

      if (route.name === 'Authenticate') {
        await router.push('/')
      } else if (oldId != null && route.name !== 'Home') {
        // Token was toggled — back to home to avoid showing information that
        // doesn't exist. Await the navigation BEFORE swapping the document
        // list: an open Debt view would otherwise briefly resolve its
        // document to undefined mid-render (crash window is cross-tab now —
        // multitab token sync fires this watcher in every tab).
        await router.push('/')
      }

      void getDocuments()
      loadTicketData()
      await getUserData()
    },
    { immediate: true }
  )

  watch(
    () => store.state.remove_document_ids,
    (ids) => {
      if (ids.length > 0) {
        for (const id of ids) {
          const index = store.state.documents.findIndex((v) => v._id === id)
          if (index !== -1) {
            store.state.documents.splice(index, 1)
          }
        }
        void setStateProperty('remove_document_ids', [])
      }
    }
  )

  // Datadog user context (old App.vue datadog_user_payload watcher, debounced)
  const datadogUserPayload = computed(() => {
    const payload = authStore.selectedToken?.payload
    return {
      id: payload?.u_id,
      // The customer token carries no email; it comes from the user resource.
      email: store.state.user.email_data?.email,
      name: payload?.u_name,
      solicitor: false,
    }
  })
  watchDebounced(
    datadogUserPayload,
    (value) => {
      if (value.id == null) return
      try {
        setDatadogUserAndAccount({ ...value, id: value.id })
      } catch (err) {
        console.error('Failed to set Datadog user:', err)
      }
    },
    { debounce: 500, immediate: true }
  )

  // Survicate identify — the survicate plugin owns init/identify/run
  // (replaced Wootric and its legacy component in ADA-4425)
  watch(
    () => [store.state.user._id, store.state.user.email_data?.email, store.state.user._created] as const,
    ([userId, email, created]) => {
      if (userId != null && email != null && email !== '' && created != null) {
        identifySurvicateUser({
          user_id: userId,
          email,
          created_at: new Date(created),
          solicitor: false,
        })
      }
    }
  )

  const onReloadTicketData = () => {
    loadTicketData()
  }
  onMounted(() => {
    bus?.on('reload_ticket_data', onReloadTicketData)
  })
  onBeforeUnmount(() => {
    bus?.off('reload_ticket_data', onReloadTicketData)
  })

  return { loading }
}
